
export class TimeSpan {

	public static Create(s: string): TimeSpan {
		let result = new TimeSpan();
		if (!result.FromString(s))
			throw new Error();
		return result;
	}

	public static FromDays(days: number): TimeSpan {
		return new TimeSpan(days, 0, 0, 0);
	}

	public static FromHours(hours: number): TimeSpan {
		return new TimeSpan(0, hours, 0, 0);
	}

	public static FromMinutes(minutes: number): TimeSpan {
		return new TimeSpan(0, 0, minutes, 0);
	}

	public static FromSeconds(seconds: number): TimeSpan {
		return new TimeSpan(0, 0, 0, seconds);
	}

	private _IsNegative: boolean;
	private _Days: number;
	private _Hours: number;
	private _Minutes: number;
	private _Seconds: number;

	public get IsNegative(): boolean {
		return this._IsNegative;
	}
	public set IsNegative(value: boolean) {
		this._IsNegative = value;
	}

	public get Days(): number {
		return (this.IsNegative ? -this._Days : this._Days);
	}
	public set Days(value: number) {
		if (typeof value !== "number" || !Number.isInteger(value) || value < 0)
			throw new Error("Invalid days value");
		this._Days = value;
	}

	public get Hours(): number {
		return (this.IsNegative ? -this._Hours : this._Hours);
	}
	public set Hours(value: number) {
		if (typeof value !== "number" || !Number.isInteger(value) || value < 0 || value >= 24)
			throw new Error("Invalid hours value");
		this._Hours = value;
	}

	public get Minutes(): number {
		return (this.IsNegative ? -this._Minutes : this._Minutes);
	}
	public set Minutes(value: number) {
		if (typeof value !== "number" || !Number.isInteger(value) || value < 0 || value >= 60)
			throw new Error("Invalid minutes value");
		this._Minutes = value;
	}

	public get Seconds(): number {
		return (this.IsNegative ? -this._Seconds : this._Seconds);
	}
	public set Seconds(value: number) {
		if (typeof value !== "number" || !Number.isFinite(value) || value < 0 || value >= 60)
			throw new Error("Invalid seconds value");
		this._Seconds = value;
	}

	constructor(days?: number, hours?: number, minutes?: number, seconds?: number) {
		this.Days = days || 0;
		this.Hours = hours || 0;
		this.Minutes = minutes || 0;
		this.Seconds = seconds || 0;
	}

	public Equals(obj: any): boolean {
		if (!obj)
			return false;

		if (!(obj instanceof TimeSpan))
			return false;
		let other = (obj as TimeSpan);

		return (this.IsNegative === other.IsNegative &&
			this.Days === other.Days &&
			this.Hours === other.Hours &&
			this.Minutes === other.Minutes &&
			this.Seconds === other.Seconds);
	}

	public get IsEmpty(): boolean {
		return !(this.Days || this.Hours || this.Minutes || this.Seconds);
	}

	public toString(): string {
		let result = "";

		if (this.IsNegative && (this.Days || this.Hours || this.Minutes || this.Seconds)) {
			result += "-";
		}

		if (this._Days > 0) {
			result += TimeSpan.FormatNumber(this._Days, 0);
			result += ".";
		}
		result += TimeSpan.FormatNumber(this._Hours, 0);
		result += ":";
		result += TimeSpan.FormatNumber(this._Minutes, 0);
		result += ":";
		result += TimeSpan.FormatNumber(this._Seconds, 7);

		return result;
	}

	public toStringNoFraction(): string {
		let result = this.toString();
		let lastPeriodIndex = result.lastIndexOf(".");
		let lastColonIndex = result.lastIndexOf(":");
		if (lastPeriodIndex >= 0 && lastPeriodIndex > lastColonIndex) {
			result = result.substr(0, lastPeriodIndex);
		}
		return result;
	}

	public ToString(): string {
		return this.toString();
	}

	public ToStringNoFraction(): string {
		return this.toStringNoFraction();
	}

	private static FormatNumber(value: number, fractionDigits: number): string {
		if (typeof value !== "number" || !Number.isFinite(value)) {
			throw new Error("Input must be a finite number");
		}

		let result: string = value.toFixed(fractionDigits).replace(/\.?0+$/, "");

		// Pad with leading zeros if the whole part has less than 2 digits.
		let wholePart = "";

		let periodIndex = result.indexOf(".");
		if (periodIndex >= 0) {
			wholePart = result.substr(0, periodIndex);
		}
		else {
			wholePart = result;
		}

		while (wholePart.length < 2) {
			wholePart = "0" + wholePart;
			result = "0" + result;
		}

		return result;
	}

	public FromString(s: string): boolean {
		try {
			if (!s)
				return false;

			let isNegative = false;
			let days: number = 0;
			let hours: number = 0;
			let minutes: number = 0;
			let seconds: number = 0;

			if (s.startsWith("-")) {
				isNegative = true;
				s = s.substr(1);
			}

			let firstColonIndex = s.indexOf(":");

			let firstPeriodIndex = s.indexOf(".");
			if (firstPeriodIndex >= 0 && firstPeriodIndex < firstColonIndex) {
				let daysStr = s.substr(0, firstPeriodIndex);
				if (!daysStr)
					return false;
				days = parseInt(daysStr);
				s = s.substr(firstPeriodIndex + 1);
			}

			let arr: string[] = s.split(":");
			if (arr.length != 3)
				return false;

			let hoursStr = arr[0];
			if (!hoursStr)
				return false;
			hours = parseInt(hoursStr);

			let minutesStr = arr[1];
			if (!minutesStr)
				return false;
			minutes = parseInt(minutesStr);

			let secondsStr = arr[2];
			if (!secondsStr)
				return false;
			seconds = parseFloat(secondsStr);

			this.IsNegative = isNegative;
			this.Days = days;
			this.Hours = hours;
			this.Minutes = minutes;
			this.Seconds = seconds;

			return true;
		}
		catch (err) {
			return false;
		}
	}
}
